import { useCallback, useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, LogOut, Bell, ChevronRight, Clock, AlertTriangle, Lock } from "lucide-react";
import { SECTIONS, ADMIN_SECTION } from "@/config/sections";
import { useAuth } from "@/context/AuthContext";
import { PlanProvider, usePlan } from "@/context/PlanContext";
import { trackRoute } from "@/lib/analytics";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import TestimonialPrompt from "@/components/TestimonialPrompt";
import AICoach from "@/components/AICoach";

const GROUPS = [
  { id: "main", label: "Overview" },
  { id: "build", label: "Build & Fund" },
  { id: "manage", label: "Manage" },
  { id: "grow", label: "Grow" },
  { id: "account", label: "Account" },
];

const navClass = ({ isActive }) =>
  `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
    isActive
      ? "bg-[#D4AF37]/12 text-white border border-[#D4AF37]/30"
      : "text-[#A1A1AA] hover:text-white hover:bg-white/5 border border-transparent"
  }`;

const OVERLAY_ANIM = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const DRAWER_ANIM = { initial: { x: "-100%" }, animate: { x: 0 }, exit: { x: "-100%" }, transition: { type: "tween", duration: 0.25 } };

function Logo() {
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo">
      <div className="w-9 h-9 rounded-lg fv-gold-gradient flex items-center justify-center shrink-0">
        <span className="font-heading text-[#0A0A0B] text-xl font-bold">F</span>
      </div>
      <div className="leading-tight">
        <div className="font-heading text-lg text-white tracking-wide">Fairview</div>
        <div className="text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]">Systems</div>
      </div>
    </div>
  );
}

const GATED_MODULES = ["formation", "credit", "funding", "contracts", "compliance", "portal"];

function NavItem({ item }) {
  const Icon = item.icon;
  const { hasModule, hasFeature, loading } = usePlan();
  const locked = !loading && (
    (GATED_MODULES.includes(item.key) && !hasModule(item.key)) ||
    (item.key === "team" && !hasFeature("team_accounts"))
  );
  return (
    <NavLink to={item.path} data-testid={`nav-${item.key}`} className={navClass}>
      <Icon className="w-[18px] h-[18px] shrink-0" />
      <span className="truncate flex-1">{item.label}</span>
      {locked && <Lock className="w-3.5 h-3.5 text-[#D4AF37]/70 shrink-0" data-testid={`nav-${item.key}-lock`} />}
    </NavLink>
  );
}

function SidebarContent({ items, isAdmin, onLogout }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center border-b border-white/8"><Logo /></div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {GROUPS.map((g) => {
          const groupItems = items.filter((i) => i.group === g.id);
          if (!groupItems.length) return null;
          return (
            <div key={g.id}>
              <div className="px-3 mb-1.5 text-[10px] tracking-[0.2em] uppercase text-[#71717A]">{g.label}</div>
              <div className="space-y-0.5">
                {groupItems.map((item) => <NavItem key={item.key} item={item} />)}
              </div>
            </div>
          );
        })}
        {isAdmin && (
          <div>
            <div className="px-3 mb-1.5 text-[10px] tracking-[0.2em] uppercase text-[#71717A]">Admin</div>
            <NavItem item={ADMIN_SECTION} />
          </div>
        )}
      </nav>
      <div className="p-3 border-t border-white/8">
        <button onClick={onLogout} data-testid="sidebar-logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#A1A1AA] hover:text-white hover:bg-white/5 transition-colors">
          <LogOut className="w-[18px] h-[18px]" /> Sign out
        </button>
      </div>
    </div>
  );
}

function TopBar({ user, unread, onMenu, onNotifications, onProfile }) {
  return (
    <header className="h-16 sticky top-0 z-30 fv-glass flex items-center justify-between px-4 sm:px-6">
      <div className="flex items-center gap-3">
        <button className="lg:hidden text-white" onClick={onMenu} data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></button>
        <div className="lg:hidden"><Logo /></div>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={onNotifications} data-testid="header-notifications-btn"
          className="relative w-10 h-10 rounded-lg hover:bg-white/5 flex items-center justify-center text-[#A1A1AA] hover:text-white transition-colors">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full fv-gold-gradient text-[#0A0A0B] text-[10px] font-bold flex items-center justify-center">{unread}</span>
          )}
        </button>
        <button onClick={onProfile} data-testid="header-profile-btn"
          className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-full hover:bg-white/5 transition-colors">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full fv-gold-gradient flex items-center justify-center text-[#0A0A0B] font-semibold text-sm">
              {(user?.name || user?.email || "U")[0].toUpperCase()}
            </div>
          )}
          <span className="hidden sm:block text-sm text-white max-w-[120px] truncate">{user?.name || user?.email}</span>
          <ChevronRight className="hidden sm:block w-4 h-4 text-[#71717A]" />
        </button>
      </div>
    </header>
  );
}

function TrialBar({ sub, onGo }) {
  if (!sub) return null;
  if (sub.status === "trialing") {
    return (
      <div className="bg-[#151310] border-b border-[#D4AF37]/25 px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3" data-testid="trial-bar">
        <div className="flex items-center gap-2 text-sm text-[#E4E4E7] min-w-0">
          <Clock className="w-4 h-4 text-[#D4AF37] shrink-0" />
          <span className="truncate"><span className="text-[#D4AF37] font-medium">{sub.days_remaining} day{sub.days_remaining !== 1 ? "s" : ""} left</span> in your free {sub.tier} trial · no credit card required</span>
        </div>
        <button onClick={onGo} data-testid="trial-bar-subscribe"
          className="shrink-0 text-xs fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-4 h-8">Subscribe</button>
      </div>
    );
  }
  if (sub.status === "expired") {
    return (
      <div className="bg-amber-500/8 border-b border-amber-500/30 px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3" data-testid="trial-expired-bar">
        <div className="flex items-center gap-2 text-sm text-[#E4E4E7] min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="truncate">Your free trial has ended. Subscribe to continue — your data is safe.</span>
        </div>
        <button onClick={onGo} data-testid="trial-expired-subscribe"
          className="shrink-0 text-xs fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-4 h-8">Subscribe</button>
      </div>
    );
  }
  return null;
}

export default function DashboardLayout() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => { trackRoute(location.pathname); }, [location.pathname]);

  useEffect(() => {
    api.get("/notifications")
      .then(({ data }) => setUnread(data.filter((n) => !n.read).length))
      .catch(() => {});
  }, [location.pathname]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { logError("DashboardLayout", "Logout request failed", err); }
    setUser(false);
    toast.success("Signed out");
    navigate("/login");
  }, [navigate, setUser]);

  const isAdmin = user?.role === "admin";

  return (
    <PlanProvider>
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/8 bg-[#0d0d0f] fixed inset-y-0 left-0">
        <SidebarContent items={SECTIONS} isAdmin={isAdmin} onLogout={logout} />
      </aside>

      <AnimatePresence>
        {open && (
          <>
            <motion.div className="lg:hidden fixed inset-0 bg-black/60 z-40"
              {...OVERLAY_ANIM} onClick={() => setOpen(false)} />
            <motion.aside className="lg:hidden fixed inset-y-0 left-0 w-72 bg-[#0d0d0f] border-r border-white/8 z-50"
              {...DRAWER_ANIM}>
              <SidebarContent items={SECTIONS} isAdmin={isAdmin} onLogout={logout} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <TopBar user={user} unread={unread} onMenu={() => setOpen(true)}
          onNotifications={() => navigate("/notifications")} onProfile={() => navigate("/profile")} />
        <TrialBar sub={user?.subscription} onGo={() => navigate("/billing")} />
        <main className="flex-1 p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
      <TestimonialPrompt />
      <AICoach />
    </div>
    </PlanProvider>
  );
}
